import { Fragment, forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { FormControl, Grid, InputAdornment, OutlinedInput } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import FieldNames from '../../defaults/FieldNames.json';
import {
    removeLeadingZeros,
    removeLetters,
    removeNegative,
    removeDecimal,
    currencyFormat,
} from '../../utils/FormatHelper';
import DetailsPopover from '../details/customPanels/DetailsPopover';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface RecordCostAssignmentGridHandle {
    getValues: () => Record<string, any>;
    isValid: () => boolean;
}

export type CostAssignmentMode = 'fuels' | 'postfire';

export interface RecordCostAssignmentGridProps {
    selectedRow?: any;
    requiredFields?: string[];
    isPoint?: boolean;
    mode?: CostAssignmentMode;
    onChange?: (values: Record<string, any>) => void;
    onValidityChange?: (valid: boolean) => void;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const BLUE_ALIEN = '#1976d2';

const theme = createTheme({
    components: {
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: BLUE_ALIEN,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: BLUE_ALIEN,
                        borderWidth: '2px',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: BLUE_ALIEN,
                        borderWidth: '2px',
                    },
                },
            },
        },
    },
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

const FF = FieldNames.fundingFields;
const FEF = FieldNames.featureFields;

/** Sanitise raw input: strip leading zeros, decimals, negatives, letters, then add commas. */
const sanitiseCurrencyInput = (raw: string): string => {
    const noLeadingZeros = removeLeadingZeros(raw);
    const noDecimals = removeDecimal(noLeadingZeros);
    const noNegatives = removeNegative(noDecimals);
    const noLetters = removeLetters(noNegatives);
    return currencyFormat(noLetters);
};

/** Resolve an initial cost value from a row, returning a formatted string or "0". */
const resolveInitialCost = (row: any, field: string): string => {
    if (!row) return '0';
    const val = row[field];
    if (val === '' || val === null || val === undefined) return '0';
    return currencyFormat(val.toString());
};

/** Parse a formatted cost string into a number. */
const parseCost = (value: string | number): number => {
    if (typeof value === 'number') return value;
    return parseInt(value.replace(/,/g, ''), 10) || 0;
};

// ─── Cost-field configuration ────────────────────────────────────────────────

interface CostFieldConfig {
    label: string;
    fundingField: string;
    featureField?: string;
}

/** Fields shown only for Fuels mode */
const FUELS_COST_FIELDS: CostFieldConfig[] = [
    {
        label: 'Personnel Cost',
        fundingField: FF.personnelCost,
        featureField: FEF.estimatedPersonnelCost,
    },
    {
        label: 'Grants Fixed Cost',
        fundingField: FF.grantsFixedCost,
        featureField: FEF.estimatedGrantsFixedCost,
    },
    {
        label: 'Asset Cost',
        fundingField: FF.assetCost,
        featureField: FEF.estimatedAssetCost,
    },
    {
        label: 'Contractual Cost',
        fundingField: FF.contractualCost,
        featureField: FEF.estimatedContractualCost
    },
    {
        label: 'Other Cost',
        fundingField: FF.otherCost,
        featureField: FEF.estimatedOtherCost,
    },
];

/** Fields shown only for Post-Fire mode */
const POSTFIRE_COST_FIELDS: CostFieldConfig[] = [
    {
        label: 'Personnel Cost',
        fundingField: FF.personnelCost,
        featureField: FEF.estimatedPersonnelCost,
    },
    {
        label: 'Equipment Cost',
        fundingField: FF.equipmentCost,
        featureField: FEF.estimatedEquipmentCost,
    },
    {
        label: 'Material and Supply Cost',
        fundingField: FF.materialAndSupplyCost,
        featureField: FEF.estimatedMaterialAndSupplyCost,
    },
    {
        label: 'Procurement Cost',
        fundingField: FF.procurementCost,
        featureField: FEF.estimatedProcurementCost,
    },
    {
        label: 'Travel Cost',
        fundingField: FF.travelCost,
        featureField: FEF.estimatedTravelCost,
    },
];

/** Returns the cost fields to render for the given mode. */
const getCostFields = (mode: CostAssignmentMode): CostFieldConfig[] =>
    mode === 'fuels' ? FUELS_COST_FIELDS : POSTFIRE_COST_FIELDS;

// ─── Sub-component: a single cost input row ──────────────────────────────────

interface CostInputRowProps {
    label: string;
    value: string;
    featureField: string;
    requiredFields: string[];
    isPoint: boolean;
    disabled?: boolean;
    onChange?: (sanitisedValue: string) => void;
}

function CostInputRow({
    label,
    value,
    featureField,
    requiredFields,
    isPoint,
    disabled = false,
    onChange,
}: CostInputRowProps) {
    return (
        <FormControl variant='standard' sx={{ margin: '15px', minWidth: 120, width: '90%' }}>
            <ThemeProvider theme={theme}>
                <Grid container direction='row' size={12} style={{ alignItems: 'center' }}>
                    <Grid size={6}>
                        <span>
                            <label>{label}</label>
                            <span className='required'>*</span>
                            <DetailsPopover
                                mode='edit'
                                field={featureField}
                                categoryValue={null}
                                isPoint={isPoint ? FieldNames.yesNo.yes : FieldNames.yesNo.no}
                                agencyValue={null}
                                isBulkApproval={false}
                            />
                        </span>
                    </Grid>
                    <Grid size={6}>
                        <OutlinedInput
                            value={value}
                            fullWidth
                            disabled={disabled}
                            onChange={(e) => onChange?.(sanitiseCurrencyInput(e.target.value))}
                            startAdornment={<InputAdornment position='start'>$</InputAdornment>}
                        />
                    </Grid>
                </Grid>
            </ThemeProvider>
        </FormControl>
    );
}

// ─── Main component ──────────────────────────────────────────────────────────
//
// Imperative handle (via ref):
//   getValues()  – returns a snapshot of all current cost field values
//   isValid()    – returns true when the computed total cost is > 0
//

const RecordCostAssignmentGrid = forwardRef<
    RecordCostAssignmentGridHandle,
    RecordCostAssignmentGridProps
>(function RecordCostAssignmentGrid(
    {
        selectedRow,
        requiredFields = [],
        isPoint = false,
        mode = 'fuels',
        onChange,
        onValidityChange,
    },
    ref
) {
    // ── Local state for each cost line ──────────────────────────────────
    const [personnelCost, setPersonnelCost] = useState('0');
    const [grantsFixedCost, setGrantsFixedCost] = useState('0');
    const [assetCost, setAssetCost] = useState('0');
    const [contractualCost, setContractualCost] = useState('0');
    const [otherCost, setOtherCost] = useState('0');
    // postfire-only fields
    const [travelCost, setTravelCost] = useState('0');
    const [equipmentCost, setEquipmentCost] = useState('0');
    const [materialAndSupplyCost, setMaterialAndSupplyCost] = useState('0');
    const [procurementCost, setProcurementCost] = useState('0');

    // ── Initialise from selectedRow ─────────────────────────────────────
    useEffect(() => {
        setPersonnelCost(resolveInitialCost(selectedRow, FF.personnelCost));
        setGrantsFixedCost(resolveInitialCost(selectedRow, FF.grantsFixedCost));
        setAssetCost(resolveInitialCost(selectedRow, FF.assetCost));
        setContractualCost(resolveInitialCost(selectedRow, FF.contractualCost));
        setOtherCost(resolveInitialCost(selectedRow, FF.otherCost));
        setTravelCost(resolveInitialCost(selectedRow, FF.travelCost));
        setEquipmentCost(resolveInitialCost(selectedRow, FF.equipmentCost));
        setMaterialAndSupplyCost(resolveInitialCost(selectedRow, FF.materialAndSupplyCost));
        setProcurementCost(resolveInitialCost(selectedRow, FF.procurementCost));
    }, [selectedRow]);

    // ── Derive total from the mode-appropriate fields (no separate state) ──
    // This avoids a race condition where a mode-change effect would zero out
    // postfire fields after the selectedRow effect had correctly initialised them.
    const totalCost = useMemo(() => {
        const activeCosts =
            mode === 'fuels'
                ? [personnelCost, grantsFixedCost, assetCost, contractualCost, otherCost]
                : [personnelCost, travelCost, equipmentCost, materialAndSupplyCost, procurementCost];
        const sum = activeCosts.reduce((acc, v) => acc + parseCost(v), 0);
        return currencyFormat(String(sum));
    }, [mode, personnelCost, grantsFixedCost, assetCost, contractualCost, otherCost,
        travelCost, equipmentCost, materialAndSupplyCost, procurementCost]);

    // ── Build a snapshot of all current values, zeroing irrelevant fields ──
    const buildValues = useCallback(
        () =>
            mode === 'fuels'
                ? {
                      [FF.personnelCost]: parseCost(personnelCost),
                      [FF.grantsFixedCost]: parseCost(grantsFixedCost),
                      [FF.assetCost]: parseCost(assetCost),
                      [FF.contractualCost]: parseCost(contractualCost),
                      [FF.otherCost]: parseCost(otherCost),
                      [FF.travelCost]: 0,
                      [FF.equipmentCost]: 0,
                      [FF.materialAndSupplyCost]: 0,
                      [FF.procurementCost]: 0,
                      [FF.totalCost]: parseCost(totalCost),
                  }
                : {
                      [FF.personnelCost]: parseCost(personnelCost),
                      [FF.grantsFixedCost]: 0,
                      [FF.assetCost]: 0,
                      [FF.contractualCost]: 0,
                      [FF.otherCost]: 0,
                      [FF.travelCost]: parseCost(travelCost),
                      [FF.equipmentCost]: parseCost(equipmentCost),
                      [FF.materialAndSupplyCost]: parseCost(materialAndSupplyCost),
                      [FF.procurementCost]: parseCost(procurementCost),
                      [FF.totalCost]: parseCost(totalCost),
                  },
        [mode, personnelCost, grantsFixedCost, assetCost, contractualCost, otherCost,
         travelCost, equipmentCost, materialAndSupplyCost, procurementCost, totalCost]
    );

    // ── Validation: total must be > 0 ───────────────────────────────────
    const checkValidity = useCallback(() => {
        return parseCost(totalCost) > 0;
    }, [totalCost]);

    // ── Notify parent whenever values change ────────────────────────────
    useEffect(() => {
        onChange?.(buildValues());
    }, [buildValues, onChange]);

    // ── Notify parent when validity changes ─────────────────────────────
    useEffect(() => {
        onValidityChange?.(checkValidity());
    }, [checkValidity, onValidityChange]);

    // ── Imperative handle ───────────────────────────────────────────────
    useImperativeHandle(
        ref,
        () => ({
            getValues: buildValues,
            isValid: checkValidity,
        }),
        [buildValues, checkValidity]
    );

    // ── Map of fundingField → setter ────────────────────────────────────
    const handleChange = useCallback(
        (fundingField: string, sanitised: string) => {
            const setters: Record<string, (v: string) => void> = {
                [FF.personnelCost]: setPersonnelCost,
                [FF.grantsFixedCost]: setGrantsFixedCost,
                [FF.assetCost]: setAssetCost,
                [FF.contractualCost]: setContractualCost,
                [FF.otherCost]: setOtherCost,
                [FF.travelCost]: setTravelCost,
                [FF.equipmentCost]: setEquipmentCost,
                [FF.materialAndSupplyCost]: setMaterialAndSupplyCost,
                [FF.procurementCost]: setProcurementCost,
            };
            const setter = setters[fundingField];
            if (setter) setter(sanitised);
        },
        []
    );

    // ── Lookup from fundingField → current display value ────────────────
    const valueMap: Record<string, string> = {
        [FF.personnelCost]: personnelCost,
        [FF.grantsFixedCost]: grantsFixedCost,
        [FF.assetCost]: assetCost,
        [FF.contractualCost]: contractualCost,
        [FF.otherCost]: otherCost,
        [FF.travelCost]: travelCost,
        [FF.equipmentCost]: equipmentCost,
        [FF.materialAndSupplyCost]: materialAndSupplyCost,
        [FF.procurementCost]: procurementCost,
    };

    // ── Render ──────────────────────────────────────────────────────────
    const activeCostFields = getCostFields(mode);

    return (
        <Fragment>
            {activeCostFields.map((cfg) => (
                <CostInputRow
                    key={cfg.fundingField}
                    label={cfg.label}
                    value={valueMap[cfg.fundingField]}
                    featureField={cfg.featureField ?? ''}
                    requiredFields={requiredFields}
                    isPoint={isPoint}
                    onChange={(sanitised) => handleChange(cfg.fundingField, sanitised)}
                />
            ))}

            {/* Total Cost — read-only */}
            <CostInputRow
                label='Total Cost'
                value={totalCost}
                featureField={FEF.estimatedTotalCost}
                requiredFields={requiredFields}
                isPoint={isPoint}
                disabled
            />
        </Fragment>
    );
});

export default RecordCostAssignmentGrid;
